/**
 * A mapping from the plural resource type found in the URL
 * to the desired singular key in the output object.
 */
const resourceTypeMap: Record<string, string> = {
  organizations: 'organization',
  workspaces: 'workspace',
  automations: 'automation',
  processes: 'process',
  apps: 'app', // legacy; prefer integrations
  integrations: 'integration',
  users: 'user',
  threads: 'thread',
  agents: 'agent',
  runs: 'run',
  exceptions: 'exception',
  events: 'event',
  guideEntries: 'guideEntry',
  revisions: 'revision',
};

// A type representing the keys of the map (e.g., 'organizations' | 'workspaces')
type PluralResourceType = keyof typeof resourceTypeMap;

// A type representing the values of the map (e.g., 'organization' | 'workspace')
type SingularResourceType = (typeof resourceTypeMap)[PluralResourceType];

/**
 * The typesafe return object. It's a partial record where keys must be one
 * of the singular resource types and values are strings.
 */
export type ParsedResource = Partial<Record<SingularResourceType, string>>;

/**
 * Parses a resource name string into an object of its components.
 *
 * @param resourceName The resource name string, e.g.,
 * /organizations/org_id/workspaces/workspace_id
 * @returns An object with singular resource names as keys and their IDs as values.
 * e.g., { organization: 'org_id', workspace: 'workspace_id'}
 */
export const parseResourceName = (resourceName?: string): ParsedResource => {
  const result: ParsedResource = {};

  if (!resourceName) return result;

  // Split the path and filter out only the empty strings (from a leading slash).
  const parts = resourceName.split('/').filter(part => part);
  // Example: ['organizations', 'org_id', 'workspaces', 'workspace_id']

  // Iterate over the parts array, taking two elements at a time (key and value).
  for (let i = 0; i < parts.length; i += 2) {
    const pluralType = parts[i];
    const id = parts[i + 1];

    if (pluralType && id) {
      // Use the map to get the singular key.
      // If not in the map, fallback to removing the trailing 's'.
      const singularType =
        resourceTypeMap[pluralType] || pluralType.replace(/s$/, '');

      result[singularType] = id;
    }
  }

  return result;
};

/**
 * Decodes a base64 encoded string
 * @param base64 - The base64 encoded string to decode
 * @returns The decoded string or empty string if input is null/undefined or invalid
 * @example
 * decodeBase64('SGVsbG8gV29ybGQ=') // returns 'Hello World'
 * decodeBase64(null) // returns ''
 */
export const decodeBase64 = (base64?: string | null) => {
  if (!base64) return '';
  try {
    return window.atob(base64);
  } catch (error) {
    console.error('Failed to decode base64 string:', error, 'Value:', base64);
    return '';
  }
};

/**
 * Extracts automation ID from automation resource name.
 * Resource name format: organizations/{orgId}/workspaces/{workspaceId}/automations/{automationId}
 *
 * @param automation - The automation resource name
 * @returns The automation ID or empty string if not found
 *
 * @example
 * getAutomationId('organizations/org-123/workspaces/ws-456/automations/auto-001')
 * // returns 'auto-001'
 */
export const getAutomationId = (automation?: string): string => {
  if (!automation) return '';
  const parsed = parseResourceName(automation);
  return parsed.automation || '';
};

export const getGuideEntryId = (guideEntry?: string): string => {
  if (!guideEntry) return '';
  const parsed = parseResourceName(guideEntry);
  return parsed.guideEntry || '';
};

/**
 * Extracts user ID from user resource name.
 * Resource name format: users/{userId}
 *
 * @param userId - The user resource name
 * @returns The user ID or empty string if not found
 *
 * @example
 * getUserId('users/user-001')
 * // returns 'user-001'
 */
export const getUserId = (userId: string): string => {
  const parsed = parseResourceName(userId);
  return parsed.user || '';
};

/**
 * Extracts revision ID from revision resource name.
 * Resource name format: organizations/{orgId}/workspaces/{workspaceId}/automations/{automationId}/revisions/{revisionId}
 *
 * @param revisionName - The revision resource name
 * @returns The revision ID or empty string if not found
 *
 * @example
 * getRevisionId('organizations/org-123/workspaces/ws-456/automations/auto-001/revisions/1.0')
 * // returns '1.0'
 */
export const getRevisionId = (revisionName?: string): string => {
  if (!revisionName) return '';
  const parsed = parseResourceName(revisionName);
  return parsed.revision || '';
};
